
import React, { useEffect } from 'react';

interface PreloadResourcesProps {
  lcpImagePath: string;
}

const PreloadResources: React.FC<PreloadResourcesProps> = ({ lcpImagePath }) => {
  useEffect(() => {
    // Use requestIdleCallback for non-critical operations
    const idleCallback = 'requestIdleCallback' in window ? 
      window.requestIdleCallback : 
      (callback: IdleRequestCallback) => setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 50 }), 1);
      
    // Adiciona preload para a imagem LCP com alta prioridade
    const preloadLCP = document.createElement('link');
    preloadLCP.rel = 'preload';
    preloadLCP.as = 'image';
    preloadLCP.href = lcpImagePath;
    preloadLCP.setAttribute('fetchpriority', 'high');
    document.head.appendChild(preloadLCP);

    // Pré-conectar com domínios críticos (apenas os mais importantes primeiro)
    const criticalDomains = [
      'fonts.googleapis.com',
      'storage.googleapis.com' // Para o vídeo da aula gratuita
    ];

    criticalDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = `https://${domain}`;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
    
    // Preload apenas para a thumbnail da primeira aula
    const firstTestimonialImg = document.createElement('link');
    firstTestimonialImg.rel = 'preload';
    firstTestimonialImg.as = 'image';
    firstTestimonialImg.href = 'https://i.ytimg.com/vi/pqTJOrWL004/hqdefault.jpg';
    firstTestimonialImg.setAttribute('fetchpriority', 'low');
    document.head.appendChild(firstTestimonialImg);
    
    // Domínios secundários e recursos adicionais (carregados com baixa prioridade)
    idleCallback(() => {
      const secondaryDomains = [
        'fonts.gstatic.com',
        'i.ytimg.com' // Para as thumbnails do YouTube
      ];
      
      secondaryDomains.forEach(domain => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = `https://${domain}`;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
      
      // Preload de recursos adicionais com delay e baixa prioridade
      const preloadResources = [
        { href: 'https://metodopps.com.br/wp-content/uploads/2024/08/mais_leite_capa.png', as: 'image' }
      ];
      
      // Adiciona depois de um delay para priorizar recursos críticos
      setTimeout(() => {
        preloadResources.forEach(resource => {
          const preload = document.createElement('link');
          preload.rel = 'preload';
          preload.as = resource.as;
          preload.href = resource.href;
          preload.setAttribute('fetchpriority', 'low');
          document.head.appendChild(preload);
        });
      }, 2000); // Atraso maior para garantir que recursos críticos sejam carregados primeiro
    });

    // Adiciona o YouTube API script de forma assíncrona quando necessário
    const addYouTubeAPI = () => {
      if (!document.getElementById('youtube-api')) {
        const script = document.createElement('script');
        script.id = 'youtube-api';
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode?.insertBefore(script, firstScript);
      }
    };

    // Adiciona o script do YouTube apenas quando o usuário interage com a página
    const addYouTubeAPIOnInteraction = () => {
      window.addEventListener('scroll', function handler() {
        window.removeEventListener('scroll', handler);
        idleCallback(addYouTubeAPI);
      }, { once: true });
      
      window.addEventListener('mousemove', function handler() {
        window.removeEventListener('mousemove', handler);
        idleCallback(addYouTubeAPI);
      }, { once: true });
    };
    
    // Inicia o processo para adicionar a API do YouTube
    addYouTubeAPIOnInteraction();

    // Cleanup
    return () => {
      const allPreloads = document.querySelectorAll('link[rel="preload"], link[rel="preconnect"]');
      allPreloads.forEach(el => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [lcpImagePath]);

  return null; // Componente não renderiza nada visualmente
};

export default PreloadResources;
